import dayjs from 'dayjs'
import qs from 'query-string'
import datacentral from '@/utils/axios/datacentral'
import {
    PaymentDateOverviewResponse,
    PaymentDateProductGroupRevenue,
    ProductGroupMonthlyRevenue,
    RevenueByPaymentDateParams,
    RevenueOverviewParams,
    RevenueOverviewResponse,
} from '../types'
import { getRevenueCombined, getRevenueByPaymentDate } from './index'

const BASE_URL = '/reports/bi'

/**
 * Export revenue combined data to Excel
 * @param params - Filter parameters
 * @returns Promise that downloads the Excel file
 */
export const exportRevenueCombined = async (
    params?: RevenueOverviewParams
): Promise<void> => {
    const queryString = qs.stringify({ ...params, export: 'excel' })
    const response = await datacentral.get(
        `${BASE_URL}/revenue-combined?${queryString}`,
        {
            responseType: 'blob',
        }
    )

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = `revenue_combined_${new Date().toISOString().split('T')[0]}.xlsx`

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
        }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

/**
 * Get revenue overview (pivot table by product group and month)
 * Fetches all data and aggregates on client-side for pivot table
 * @param params - Filter parameters
 * @returns Promise with aggregated revenue overview
 */
export const getRevenueOverview = async (
    params?: RevenueOverviewParams
): Promise<RevenueOverviewResponse> => {
    // Fetch all revenue data with high page_size
    const response = await getRevenueCombined({
        ...params,
        product_group_names: params?.product_group_names,
        sale_manager_codes: params?.sale_manager_codes,
        account_manager_codes: params?.account_manager_codes,
        is_projected: params?.is_actual_revenue ? false : undefined,
        page: 1,
        page_size: 10000, // Get all records
        sort_by: 'product_group_name',
        sort_order: 'asc',
    })

    const results = response?.results || []

    // Generate months list from params
    const months: string[] = []
    if (params?.gte && params?.lte) {
        const startDate = dayjs(params.gte)
        const endDate = dayjs(params.lte)
        let current = startDate
        while (
            current.isBefore(endDate) ||
            current.format('YYYY-MM') === endDate.format('YYYY-MM')
        ) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }
    }

    // Group by product_group_name
    const productGroupMap = new Map<string, ProductGroupMonthlyRevenue>()

    const makeEmptyGroup = (name: string): ProductGroupMonthlyRevenue => ({
        product_group_name: name,
        monthly_data: {},
        allocated_renewal: 0,
        allocated_new_sale: 0,
        allocated_upsell: 0,
        unallocated_renewal: 0,
        unallocated_new_sale: 0,
        unallocated_upsell: 0,
        total_revenue: 0,
    })

    /** Accumulate amount into the correct revenue_type field */
    const addByRevenueType = (
        group: ProductGroupMonthlyRevenue,
        prefix: 'allocated' | 'unallocated',
        revenueType: string | undefined,
        amount: number
    ) => {
        const key =
            `${prefix}_${revenueType}` as keyof ProductGroupMonthlyRevenue
        if (key in group && typeof group[key] === 'number') {
            ;(group[key] as number) += amount
        }
    }

    results.forEach((item) => {
        const groupName = item.product_group_name || 'Khác'
        if (!productGroupMap.has(groupName)) {
            productGroupMap.set(groupName, makeEmptyGroup(groupName))
        }

        const group = productGroupMap.get(groupName)!
        const amount = item.allocated_amount || item.revenue_amount || 0
        const month = item.revenue_month

        if (!group.monthly_data[month]) {
            group.monthly_data[month] = {
                allocated_previous_year: 0,
                allocated_current_year: 0,
                unallocated: 0,
            }
        }

        if (item.is_allocated) {
            if (item.is_from_previous_year) {
                group.monthly_data[month].allocated_previous_year += amount
            } else {
                group.monthly_data[month].allocated_current_year += amount
            }
            addByRevenueType(group, 'allocated', item.revenue_type, amount)
        } else {
            group.monthly_data[month].unallocated += amount
            addByRevenueType(group, 'unallocated', item.revenue_type, amount)
        }

        group.total_revenue += amount
    })

    const productGroups = Array.from(productGroupMap.values()).sort((a, b) =>
        a.product_group_name.localeCompare(b.product_group_name)
    )

    const grandTotal = productGroups.reduce(
        (sum, g) => sum + g.total_revenue,
        0
    )

    return {
        product_groups: productGroups,
        months,
        grand_total: grandTotal,
    }
}

/**
 * Get payment date overview (pivot by product group × payment month × revenue type)
 * Groups paid revenue by payment_date month and revenue_type
 */
export const getPaymentDateOverview = async (
    params?: RevenueByPaymentDateParams
): Promise<PaymentDateOverviewResponse> => {
    const response = await getRevenueByPaymentDate({
        ...params,
        page: 1,
        page_size: 10000,
        sort_by: 'product_group_name',
        sort_order: 'asc',
    })

    const results = response?.results || []

    // Generate months list from params (from_date/to_date are YYYY-MM-DD)
    const months: string[] = []
    if (params?.from_date && params?.to_date) {
        const startDate = dayjs(params.from_date).startOf('month')
        const endDate = dayjs(params.to_date).endOf('month')
        let current = startDate
        while (
            current.isBefore(endDate) ||
            current.format('YYYY-MM') === endDate.format('YYYY-MM')
        ) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }
    }

    // Group by product_group_name, then by payment month and revenue type
    const productGroupMap = new Map<string, PaymentDateProductGroupRevenue>()

    results.forEach((item) => {
        const groupName = item.product_group_name || 'Khác'
        if (!productGroupMap.has(groupName)) {
            productGroupMap.set(groupName, {
                product_group_name: groupName,
                monthly_data: {},
                total_renewal: 0,
                total_new_sale: 0,
                total_upsell: 0,
                total_revenue: 0,
            })
        }

        const group = productGroupMap.get(groupName)!
        const amount = item.allocated_amount || item.revenue_amount || 0
        // Use payment_date month instead of revenue_month
        const paymentMonth = item.payment_date
            ? dayjs(item.payment_date).format('YYYY-MM')
            : null

        if (!paymentMonth) return

        // Initialize month data if not exists
        if (!group.monthly_data[paymentMonth]) {
            group.monthly_data[paymentMonth] = {
                renewal: 0,
                new_sale: 0,
                upsell: 0,
            }
        }

        // Accumulate by revenue type
        if (item.revenue_type === 'renewal') {
            group.monthly_data[paymentMonth].renewal += amount
            group.total_renewal += amount
        } else if (item.revenue_type === 'new_sale') {
            group.monthly_data[paymentMonth].new_sale += amount
            group.total_new_sale += amount
        } else if (item.revenue_type === 'upsell') {
            group.monthly_data[paymentMonth].upsell += amount
            group.total_upsell += amount
        }

        group.total_revenue += amount
    })

    const productGroups = Array.from(productGroupMap.values()).sort((a, b) =>
        a.product_group_name.localeCompare(b.product_group_name)
    )

    const grandTotal = productGroups.reduce(
        (sum, g) => sum + g.total_revenue,
        0
    )

    return {
        product_groups: productGroups,
        months,
        grand_total: grandTotal,
    }
}

/**
 * Compare accounting Excel with system revenue data.
 * Uploads file, returns comparison Excel workbook download.
 */
export const compareAccountingRevenue = async (
    reportMonth: string,
    file: File
): Promise<void> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await datacentral.post(
        `${BASE_URL}/revenue-compare-accounting?report_month=${reportMonth}`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'blob',
        }
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    const contentDisposition = response.headers['content-disposition']
    let filename = `so_sanh_ke_toan_${reportMonth}.xlsx`
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/)
        if (match?.[1]) filename = match[1]
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}
